import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Link, Route, Routes, useParams } from "react-router";

import {
  ExternalLinkIcon,
  LightbulbIcon,
  SearchIcon,
  StarIcon,
  UploadIcon,
  UserIcon,
} from "lucide-react";

import { socialMediaLinks } from "simplydo/core/users";
import type { OpenAPI } from "simplydo/interfaces";
import type { UserProfilePrepared } from "simplydo/interfaces/types/userTypes";
import { useTheme } from "simplydo/react/theme";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { BRAND_ICONS } from "simplydo/react/ui/brand-icon";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H1, H5 } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import {
  ENTITY_TOOLBAR_HEIGHT,
  EntityBannerMedia,
  EntityCover,
  EntityHeaderContent,
  EntityHeaderMain,
  EntityHeaderMeta,
  EntityHeaderOverlap,
  EntityHeaderTagline,
  EntityHeaderTitles,
} from "components/lib/EntityHeader";
import PageLoadError from "components/lib/PageLoadError";
import ReportContentChip from "components/lib/ReportContent/ReportContentChip";
import { SideNav } from "components/lib/SideNav";
import type { SideNavItemProps } from "components/lib/SideNav/SideNavComponent";
import { NAV_BAR_HEIGHT } from "components/lib/UI";
import UserToolbar from "components/lib/UserToolbar";
import AchievementAwarder from "components/lib/Users/AchievementAwarder";

import { ProfileDetails } from "./Details/Page";
import ProfileFollowing from "./Following";
import ProfileIdeas from "./ProfileIdeas";
import type {
  ProfileUpload,
  PublicUser,
  PublicUserIdea,
  SocialMediaKey,
  UserIdeaOptions,
} from "./types";

const Profile = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);

  const [profileUser, setProfileUser] = useState<PublicUser | null>(null);
  const [ideas, setIdeas] = useState<PublicUserIdea[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [ideaOptions, setIdeaOptions] = useState<UserIdeaOptions>({
    filter: "",
    showDraft: true,
    showSubmitted: true,
    sort: "newest",
  });
  const [achievementAwardModalOpen, setAchievementAwardOpen] = useState(false);
  const { filter, showDraft, showSubmitted, sort } = ideaOptions;

  const { id: matchId } = useParams<{ id: string }>();
  const theme = useTheme();

  const getUser = useCallback((id: string) => {
    setLoading(true);
    setErrorMessage("");
    api.users.get(
      id,
      (u) => {
        setProfileUser(u);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
      },
    );
  }, []);

  const refreshAchievements = useCallback(() => {
    if (!matchId) return;
    api.users.get(
      matchId,
      (data) => {
        setProfileUser((prevUser) =>
          prevUser
            ? { ...prevUser, customAchievements: data.customAchievements }
            : null,
        );
      },
      (err) => setErrorMessage(err.message),
    );
  }, [matchId]);

  const fetchIdeas = useCallback((id: string) => {
    setIdeasLoading(true);
    api.users.ideas(
      id,
      null,
      (data) => {
        setIdeasLoading(false);
        setIdeas(Array.isArray(data) ? [] : data.ideas);
      },
      () => setIdeasLoading(false),
    );
  }, []);

  useEffect(() => {
    if (!matchId) return;
    getUser(matchId);
    fetchIdeas(matchId);
  }, [matchId, fetchIdeas, getUser]);

  const setFilters = useCallback((filters: Partial<UserIdeaOptions>) => {
    setIdeaOptions((prev) => ({ ...prev, ...filters }));
  }, []);

  const checkURL = (url: string) =>
    url.indexOf("http") === 0 ? url : `http://${url}`;

  const ProfileIdeaFilters = useMemo(
    () => (
      <div style={{ padding: 0, marginTop: 20 }}>
        <FormField>
          <FormLabel>{t("filter.title")}</FormLabel>
          <Input
            fluid
            placeholder={t("filter.search")}
            icon={SearchIcon}
            value={filter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFilters({ filter: e.target.value })
            }
          />
        </FormField>
        <Divider hidden />
        <FormField>
          <Checkbox
            checked={showDraft}
            onChange={() => setFilters({ showDraft: !showDraft })}
          >
            {t("ideas.yours.sidebar.draft")}
          </Checkbox>
          <Checkbox
            checked={showSubmitted}
            onChange={() => setFilters({ showSubmitted: !showSubmitted })}
          >
            {t("ideas.yours.sidebar.submitted")}
          </Checkbox>
        </FormField>
        <Divider hidden />
        <FormField>
          <Checkbox
            type="radio"
            name="sort"
            value="newest"
            checked={sort === "newest"}
            onChange={() => setFilters({ sort: "newest" })}
          >
            {t("order.newest")}
          </Checkbox>
          <Checkbox
            type="radio"
            name="sort"
            value="oldest"
            checked={sort === "oldest"}
            onChange={() => setFilters({ sort: "oldest" })}
          >
            {t("order.oldest")}
          </Checkbox>
        </FormField>
      </div>
    ),
    [filter, setFilters, showDraft, showSubmitted, sort, t],
  );

  if (errorMessage)
    return (
      <PageLoadError
        title={t("users.profile.problem")}
        message={errorMessage}
      />
    );

  if (!profileUser) {
    return null;
  }

  const {
    profile = {} as UserProfilePrepared,
    ownerOrganisation,
    organisation,
  } = profileUser;
  const {
    fullName,
    tagline,
    website,
    department,
    jobTitle,
    avatar,
    uploads = [],
  } = profile;
  const isYourProfile = profileUser._id === user?._id;

  const profileSocialElements = (
    [
      "facebook",
      "twitter",
      "linkedin",
      "instagram",
      "github",
    ] as SocialMediaKey[]
  ).filter((key) => !!profile[key]);

  const savedSamlMappings = user?.ownerOrganisation?.auth?.saml?.customMappings;
  const samlMappings = Array.isArray(savedSamlMappings)
    ? savedSamlMappings
    : Object.keys(savedSamlMappings ?? {}).map((localKey) => ({
        localKey,
        displayName: localKey,
      }));

  const bannerImage = util.getThumbnail(profile, "coverImage", "2560x400");

  const profileHeader = (
    <EntityHeaderContent fluid>
      <div>
        <EntityBannerMedia
          image={bannerImage}
          height={theme.sizes.isMobile ? 120 : 200}
        />
        <EntityHeaderOverlap style={bannerImage ? undefined : { marginTop: 0 }}>
          <EntityCover
            image={util.mixinCssUrlFallback(
              util.avatarUrl(profileUser, "300x300"),
              util.avatarUrl(),
            )}
            style={{ borderRadius: "50%", backgroundColor: "white" }}
          />
          <EntityHeaderMain
            style={
              !bannerImage || theme.sizes.isMobile
                ? { paddingTop: 0 }
                : undefined
            }
          >
            <EntityHeaderTitles>
              <H1>{fullName}</H1>
              {jobTitle || department ? (
                <EntityHeaderTagline>
                  {[jobTitle, department].filter(Boolean).join(" · ")}
                </EntityHeaderTagline>
              ) : null}
              {tagline ? (
                <EntityHeaderTagline
                  style={{ fontStyle: "italic", maxWidth: 640 }}
                >
                  {tagline}
                </EntityHeaderTagline>
              ) : null}
              <EntityHeaderMeta>
                {ownerOrganisation ? (
                  <span>
                    {t("users.profile.memberOf")}{" "}
                    <strong>{ownerOrganisation.name}</strong>
                  </span>
                ) : null}
                {website ? (
                  <a
                    href={checkURL(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      maxWidth: "100%",
                    }}
                  >
                    <Icon icon={ExternalLinkIcon} />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {website}
                    </span>
                  </a>
                ) : null}
                {isYourProfile && !avatar ? (
                  <Link to="/preferences/profile">
                    <Icon icon={UploadIcon} /> {t("users.profile.uploadAvatar")}
                  </Link>
                ) : null}
              </EntityHeaderMeta>
            </EntityHeaderTitles>
          </EntityHeaderMain>
        </EntityHeaderOverlap>
      </div>
    </EntityHeaderContent>
  );

  return (
    <div>
      <Helmet title={fullName} />
      {user?.ownerOrganisation?.achievementsEnabled !== false &&
      util.hasPermission(
        user,
        "org.viewDashboard",
        user?.ownerOrganisation?._id,
      ) ? (
        <AchievementAwarder
          targetUser={achievementAwardModalOpen ? profileUser : null}
          user={user}
          onClose={() => {
            setAchievementAwardOpen(false);
            refreshAchievements();
          }}
        />
      ) : null}
      <UserToolbar
        profileUser={profileUser}
        onAwardAchievement={() => setAchievementAwardOpen(true)}
      />
      <SideNav
        navItems={
          [
            {
              key: "profile",
              content: t("generic.profile"),
              iconLeft: UserIcon,
              to: "..",
              active: window.location.pathname === `/users/${profileUser._id}`,
            },
            util.hasPermission(user, "org.viewDashboard", organisation) ||
            profileUser._id === user?._id
              ? {
                  key: "ideas",
                  content: t("generic.ideas"),
                  iconLeft: LightbulbIcon,
                  to: "../ideas",
                  active:
                    window.location.pathname ===
                    `/users/${profileUser._id}/ideas`,
                }
              : null,
            util.hasPermission(user, "org.viewDashboard", organisation) ||
            profileUser._id === user?._id
              ? {
                  key: "following",
                  content: "Following",
                  iconLeft: StarIcon,
                  to: "../following",
                  active:
                    window.location.pathname ===
                    `/users/${profileUser._id}/following`,
                }
              : null,

            ...(window.location.pathname === `/users/${profileUser._id}`
              ? [
                  profileSocialElements.length
                    ? {
                        key: "links",
                        type: "custom",
                        content: (
                          <H5 style={{ textTransform: "uppercase" }}>
                            {t("generic.links")}
                          </H5>
                        ),
                      }
                    : null,

                  ...profileSocialElements.map((socialElement) => {
                    const linkConfig = socialMediaLinks[socialElement];
                    const BrandIcon =
                      "brand" in linkConfig
                        ? BRAND_ICONS[linkConfig.brand]
                        : undefined;
                    return {
                      key: socialElement,
                      type: "link" as const,
                      iconLeft: BrandIcon ? (
                        <Icon
                          icon={BrandIcon}
                          style={{ color: linkConfig.color }}
                        />
                      ) : "icon" in linkConfig ? (
                        linkConfig.icon
                      ) : undefined,
                      iconLeftAlt:
                        "iconLeftAlt" in linkConfig
                          ? linkConfig.iconLeftAlt
                          : undefined,
                      content: linkConfig.content,
                      to: linkConfig.link + (profile[socialElement] ?? ""),
                      external: true,
                    };
                  }),

                  (uploads as ProfileUpload[]).length
                    ? {
                        key: "uploads",
                        type: "custom",
                        content: (
                          <H5 style={{ textTransform: "uppercase" }}>
                            Uploads
                          </H5>
                        ),
                      }
                    : null,

                  ...(uploads as ProfileUpload[]).map((upload) => ({
                    key: upload.storedName,
                    type: "link" as const,
                    iconLeft: util.getFileIcon(upload.storedName) as IconData,
                    external: true,
                    content: upload.title,
                    to: upload.url ?? "",
                  })),
                ].filter((item) => Boolean(item))
              : []),

            window.location.pathname === `/users/${profileUser._id}/ideas`
              ? {
                  key: "ideaFilters",
                  type: "custom",
                  content: theme.sizes.isMobile ? (
                    <Accordion>
                      <AccordionTitle index={0}>
                        {`${t("generic.idea")} filters`}
                      </AccordionTitle>
                      <AccordionContent index={0}>
                        {ProfileIdeaFilters}
                      </AccordionContent>
                    </Accordion>
                  ) : (
                    ProfileIdeaFilters
                  ),
                }
              : null,
          ].filter((item) => Boolean(item)) as SideNavItemProps[]
        }
        top={NAV_BAR_HEIGHT + ENTITY_TOOLBAR_HEIGHT}
        scrollTargetRef={headerRef}
        main={
          <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
            <div ref={headerRef}>{profileHeader}</div>
            <Routes>
              <Route
                path="/"
                element={
                  <ProfileDetails
                    loading={loading}
                    profileUser={profileUser}
                    isYourProfile={isYourProfile}
                  />
                }
              />
              {(util.hasPermission(user, "org.viewDashboard", organisation) ||
                profileUser._id === user?._id) && (
                <Route
                  path="ideas"
                  element={
                    <ProfileIdeas
                      ideas={ideas}
                      loading={ideasLoading}
                      sort={sort}
                      filter={filter}
                      showDraft={showDraft}
                      showSubmitted={showSubmitted}
                      profileUser={profileUser}
                    />
                  }
                />
              )}
              {(util.hasPermission(user, "org.viewDashboard", organisation) ||
                profileUser._id === user?._id) && (
                <Route
                  path="following"
                  element={
                    <ProfileFollowing
                      loading={loading}
                      profileUser={profileUser}
                    />
                  }
                />
              )}
            </Routes>
          </div>
        }
        afterContent={
          <>
            <Divider />
            {samlMappings.length > 0 &&
            profileUser.profile?.attributes &&
            Object.keys(profileUser.profile.attributes).length > 0 ? (
              <div style={{ marginBottom: 10 }}>
                {samlMappings.map(
                  ({ localKey, displayName }) =>
                    localKey &&
                    profileUser.profile?.attributes?.[localKey] && (
                      <div key={localKey} style={{ marginBottom: 5 }}>
                        {displayName || localKey}:{" "}
                        {profileUser.profile?.attributes?.[localKey]}
                      </div>
                    ),
                )}
              </div>
            ) : null}
            <ReportContentChip forType="user" forId={profileUser._id} />
          </>
        }
      />
    </div>
  );
};

export default Profile;
